import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../../../store/hooks';
import { updateBookData, clearError } from '../../../../store/slices/bookSlice';
import { useBookAPI } from '../../../../hooks/useBookAPI';
import ContributorsSection from './components/ContributorsSection';
import { trackBookInfoLanded, trackBookInfoClicked, trackBookInfo, trackIsbnRequired } from '../../../../utils/analytics';
import { useAlert } from '../../../../components/AlertBanner';
import { useEffect } from 'react';

const languages = [
  { name: 'English', beta: false },
  { name: 'Hindi', beta: true },
  { name: 'Bengali', beta: true },
  { name: 'Tamil', beta: true },
  { name: 'Telugu', beta: true },
  { name: 'Kannada', beta: true },
  { name: 'Malayalam', beta: true },
  { name: 'Urdu', beta: true },
];

const BookInfoStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const bookData = useAppSelector((state) => state.book.bookData);
  const isStep1Complete = useAppSelector((state) => state.book.isStep1Complete);
  const { saveBook, saveAndContinue, isSaving, error } = useBookAPI();
  const [isbnError, setIsbnError] = useState('');

  useEffect(() => {
    trackBookInfoLanded('Book Info');
  }, []);

  const validateISBN = (isbn: string): boolean => {
    const digitsOnly = isbn.replace(/[-\s]/g, '');
    return digitsOnly.length === 10 || digitsOnly.length === 13;
  };

  const handleSaveToDrafts = async () => {
    const result = await saveBook();
    if (result.meta.requestStatus === 'fulfilled') showAlert('Saved to drafts successfully!', 'success');
  };

  const handleSaveAndContinue = async () => {
    if (!isStep1Complete) { showAlert('Please fill in all required fields: Language, Book Title, and Author Name', 'warning'); return; }
    if (bookData.hasISBN) {
      if (!bookData.isbn.trim()) { setIsbnError('Please enter an ISBN number'); return; }
      if (!validateISBN(bookData.isbn)) { setIsbnError('ISBN must be 10 or 13 digits'); return; }
    }
    setIsbnError('');
    trackBookInfo({
      language: bookData.language,
      title: bookData.title,
      subtitle: bookData.subtitle || '',
      author: bookData.author,
      contributors: bookData.contributors?.map((c: any) => c.name).join(', ') || '',
    });
    trackIsbnRequired(bookData.hasISBN ? 'yes' : 'no');
    await saveAndContinue('book-form-factor');
  };

  return (
    <div className="bg-white md:bg-transparent min-h-screen md:min-h-0">
      <div className="hidden md:block mb-6">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-700 hover:text-gray-900"><span>←</span><span>Go Back to Dashboard</span></button>
      </div>

      <div className="md:hidden bg-gray-50 px-4 py-3 border-b border-gray-200">
        <p className="text-xs text-gray-600">Book Info › Book Form Factor › Book Formatting</p>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4 mx-4 md:mx-0">
          <div className="flex justify-between items-start">
            <p className="text-red-700 text-sm">{error}</p>
            <button onClick={() => dispatch(clearError())} className="text-red-600 hover:text-red-800 text-sm underline ml-4">Dismiss</button>
          </div>
        </div>
      )}

      <div className="bg-white md:bg-white p-4 md:p-8 rounded-none md:rounded-lg md:border md:border-gray-200 md:shadow-sm">
        <div className="space-y-4 md:space-y-5">
          {/* Language Selection */}
          <div>
            <label className="block text-gray-900 text-sm font-medium mb-2">Select Book Language</label>
            <div className="md:hidden">
              <select value={bookData.language || ''} onChange={(e) => { dispatch(updateBookData({ language: e.target.value })); trackBookInfoClicked(e.target.value); }} className="w-full bg-white text-gray-900 px-3 py-2.5 rounded-md border border-gray-300 focus:outline-none focus:border-orange-500">
                <option value="" disabled>Select</option>
                {languages.map((lang) => (
                  <option key={lang.name} value={lang.name}>
                    {lang.name}{lang.beta ? ' (Beta)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="hidden md:grid grid-cols-4 gap-3">
              {languages.map((lang) => (
                <button key={lang.name} onClick={() => { dispatch(updateBookData({ language: lang.name })); trackBookInfoClicked(lang.name); }} className={`px-4 py-2.5 rounded-lg text-sm font-medium ${bookData.language === lang.name ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'}`}>
                  {lang.name}
                </button>
              ))}
            </div>
            <p className="hidden md:block text-xs text-gray-500 italic mt-2">* Languages other than English are currently in beta</p>
          </div>

          {/* Book Title */}
          <div>
            <label className="block text-gray-900 text-sm font-medium mb-2">Book Title</label>
            <input type="text" value={bookData.title} onChange={(e) => dispatch(updateBookData({ title: e.target.value }))} className="w-full bg-gray-50 text-gray-900 px-3 py-2.5 rounded-md border border-gray-300 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:bg-white" placeholder="Enter here" />
          </div>

          {/* Book Subtitle */}
          <div>
            <label className="block text-gray-900 text-sm font-medium mb-2">Book Subtitle</label>
            <input type="text" value={bookData.subtitle} onChange={(e) => dispatch(updateBookData({ subtitle: e.target.value }))} className="w-full bg-gray-50 text-gray-900 px-3 py-2.5 rounded-md border border-gray-300 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:bg-white" placeholder="Enter here" />
          </div>

          {/* Author Name */}
          <div>
            <label className="block text-gray-900 text-sm font-medium mb-2">Author Name</label>
            <input type="text" value={bookData.author} onChange={(e) => dispatch(updateBookData({ author: e.target.value }))} className="w-full bg-gray-50 text-gray-900 px-3 py-2.5 rounded-md border border-gray-300 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:bg-white" placeholder="Enter here" />
          </div>

          {/* Contributors */}
          <ContributorsSection />

          {/* ISBN Section */}
          <div>
            <label className="block text-gray-900 text-sm font-medium mb-2">Do you have ISBN?</label>
            {bookData.isbnAssignedByAdmin ? (
              <div className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-md">
                <span className="text-sm font-mono text-gray-900 tracking-wider">{bookData.isbn}</span>
                <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full font-medium">Assigned by IndiePress</span>
              </div>
            ) : (
              <div className="flex flex-col md:flex-row gap-2">
                <select value={bookData.hasISBN ? 'yes' : 'no'} onChange={(e) => { dispatch(updateBookData({ hasISBN: e.target.value === 'yes' })); if (e.target.value === 'no') dispatch(updateBookData({ isbn: '' })); setIsbnError(''); }} className="w-full md:w-auto bg-gray-50 text-gray-900 px-3 py-2.5 rounded-md border border-gray-300">
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
                <input type="text" value={bookData.isbn} onChange={(e) => { dispatch(updateBookData({ isbn: e.target.value })); setIsbnError(''); }} disabled={!bookData.hasISBN} className={`flex-1 bg-gray-50 text-gray-900 px-3 py-2.5 rounded-md border focus:outline-none ${!bookData.hasISBN ? 'opacity-50 cursor-not-allowed border-gray-300' : isbnError ? 'border-red-500' : 'border-gray-300 focus:border-orange-500'}`} placeholder="Enter ISBN" />
              </div>
            )}
            {isbnError && <p className="text-red-500 text-sm mt-1">{isbnError}</p>}
          </div>

          {/* Action Buttons */}
          <div className="md:hidden flex flex-col items-center space-y-3 pt-4">
            <button onClick={handleSaveToDrafts} disabled={isSaving} className="bg-white text-orange-500 px-8 py-3 rounded-md border-2 border-orange-500 font-medium disabled:opacity-50">{isSaving ? 'Saving...' : 'Save'}</button>
            <button onClick={handleSaveAndContinue} disabled={isSaving} className="bg-gray-900 text-white px-8 py-3 rounded-md font-medium disabled:opacity-50">{isSaving ? 'Saving...' : 'Continue'}</button>
          </div>
          <div className="hidden md:flex gap-3 pt-4 justify-start">
            <button onClick={handleSaveToDrafts} disabled={isSaving} className="bg-transparent text-orange-500 px-8 py-3 rounded-lg border-2 border-orange-500 font-medium disabled:opacity-50">{isSaving ? 'Saving...' : 'Save'}</button>
            <button onClick={handleSaveAndContinue} disabled={isSaving} className="bg-gray-900 text-white px-8 py-3 rounded-lg font-medium disabled:opacity-50">{isSaving ? 'Saving...' : 'Continue'}</button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default BookInfoStep;
